import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadReportPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, FileText, Download, CalendarClock, Bot, User } from "lucide-react";

const SUGGESTIONS = [
  "How many hot tubs do we have in stock?",
  "Show me web shop sales for the latest week in the data",
  "Build me a sales + hot tub, swim spa and sauna stock report",
  "Email Ross a report every Monday morning with sales and stock",
];

export default function AssistantChat({ onScheduled }) {
  const sessionId = useRef(
    (window.crypto?.randomUUID && window.crypto.randomUUID()) || String(Date.now())
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: msg,
        session_id: sessionId.current,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: data.answer, report: data.report, scheduled: data.scheduled },
      ]);
      if (data.scheduled) {
        toast.success(`Scheduled: emails ${data.scheduled.recipients.join(", ")} every ${data.scheduled.day}`);
        onScheduled && onScheduled();
      }
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: e.response?.data?.detail || "Sorry, something went wrong.", error: true },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col h-[560px] overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </span>
        <div>
          <h2 className="text-base font-bold text-slate-900 leading-tight">Reporting Assistant</h2>
          <p className="text-xs text-slate-500">Ask about sales, stock or schedule email reports</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="assistant-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <span className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center mb-3">
              <Bot className="w-6 h-6" />
            </span>
            <p className="text-sm text-slate-500 max-w-xs">
              Ask me anything about your operations data, or tell me to build and email reports.
            </p>
            <div className="mt-5 flex flex-col gap-2 w-full max-w-sm">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="assistant-suggestion"
                  className="text-left text-sm text-slate-700 bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-lg px-3 py-2 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <span className="w-7 h-7 shrink-0 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center mt-0.5">
                <Bot className="w-4 h-4" />
              </span>
            )}
            <div
              className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-indigo-600 text-white rounded-br-sm"
                  : m.error
                  ? "bg-rose-50 text-rose-700 border border-rose-200"
                  : "bg-slate-50 text-slate-800 border border-slate-200 rounded-bl-sm"
              }`}
            >
              {m.text}
              {m.report && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/reports/${m.report.id}`}
                    data-testid="assistant-view-report"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" /> View report
                  </Link>
                  <button
                    onClick={() => downloadReportPdf(m.report.id, m.report.title).catch(() => toast.error("PDF failed"))}
                    data-testid="assistant-download-pdf"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </button>
                </div>
              )}
              {m.scheduled && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-lg">
                  <CalendarClock className="w-3.5 h-3.5" /> Scheduled · {m.scheduled.day} {String(m.scheduled.hour).padStart(2, "0")}:00
                </div>
              )}
            </div>
            {m.role === "user" && (
              <span className="w-7 h-7 shrink-0 rounded-lg bg-slate-200 text-slate-600 flex items-center justify-center mt-0.5">
                <User className="w-4 h-4" />
              </span>
            )}
          </div>
        ))}

        {sending && (
          <div className="flex gap-2.5">
            <span className="w-7 h-7 shrink-0 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </span>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-3.5 py-2.5">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-2 px-4 py-3 border-t border-slate-100"
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about sales, stock, or request a report…"
          data-testid="assistant-input"
          disabled={sending}
          className="flex-1"
        />
        <Button
          type="submit"
          disabled={sending || !input.trim()}
          data-testid="assistant-send"
          className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
